import type { UseProductCardProps } from '@maestro/commerce-blocks/components/product-card/product-card.types'

import { useTranslations } from 'next-intl'
import { useCallback, useMemo } from 'react'

import { buildImageUrl } from '@maestro/commerce-blocks/components/image-product/utils/build-image-url'
import { useGlobalContext } from '@maestro/commerce-blocks/context/global.context'
import { useMaestroSession } from '@maestro/commerce-blocks/providers/maestro-session/maestro-session.context'
import { useWishlistContext } from '@maestro/commerce-blocks/providers/wishlist/wishlist.context'
import { useEventBusContext } from '@maestro/core/event/event-bus.context'
import {
  addProductQueryId,
  removeProductQueryId,
} from '@maestro/core/event/queryid-tracker.utils'
import {
  type EventPayloadType,
  EventType,
  type Product,
  type ProductVariant,
} from '@maestro/data-layer'
import { theme } from '@maestro/local/theme'

export const useProductCard = ({
  product,
  displayStockStatus,
  recommendationProps: { trackSlotClick, productSlotMap } = {},
  eventProps,
}: UseProductCardProps) => {
  const { searchProductIndex, setPreloadedProductData } = useGlobalContext()
  const { toggleProductInWishlist, isProductInWishlist } = useWishlistContext()
  const { images } = theme()
  const t = useTranslations('commerce-blocks')
  const { publish } = useEventBusContext()
  const { session } = useMaestroSession()
  const { listId, listName, listType } = eventProps.eventListProps || {}
  const { searchIndex, queryID } = eventProps.eventSearchProps || {}

  const eventPayload: EventPayloadType[EventType.PRODUCT_CLICKED] =
    useMemo(() => {
      return {
        item: {
          product,
          quantity: 1,
          position: eventProps.productIndex,
        },
        listId,
        listName: listName ?? '',
        listType: listType ?? '',
        searchServiceIndex: searchIndex || searchProductIndex,
        queryID,
        authenticatedUserToken: session?.user?.hashedEmail ?? '',
      }
    }, [
      eventProps.productIndex,
      listId,
      listName,
      listType,
      product,
      queryID,
      searchIndex,
      searchProductIndex,
      session?.user?.hashedEmail,
    ])

  const isInWishlist = isProductInWishlist(product.id)

  const handleWishlistToggle = useCallback(() => {
    if (!isInWishlist) {
      publish<EventPayloadType[EventType.PRODUCT_ADDED_TO_WISHLIST]>(
        EventType.PRODUCT_ADDED_TO_WISHLIST,
        eventPayload
      )
    }

    toggleProductInWishlist(product)
  }, [isInWishlist, eventPayload, product, publish, toggleProductInWishlist])

  const productImage = useMemo(() => {
    return (
      product.image || {
        type: 'image' as const,
        url: images.placeholderNoImageSrc,
      }
    )
  }, [images.placeholderNoImageSrc, product.image])

  const stockStatus = useMemo(() => {
    if (!displayStockStatus || !product.stock) {
      return null
    }
    return product.stock
  }, [displayStockStatus, product.stock])

  const setProductData = useCallback(() => {
    // Build the actual PLP image URL (with exact same params) so PDP can use it from browser cache
    const cachedUrl = buildImageUrl({
      src: productImage.url,
      width: images.plp.width,
      fit: images.fit,
      quality: images.quality,
    })

    // Add cachedUrl to image and gallery items for PDP placeholder
    // Match by URL (not index) since gallery order may differ from main image position
    const imageWithCache = { ...productImage, cachedUrl }
    const galleryWithCache = product.gallery?.map((item) =>
      item.type === 'image' && item.url === productImage.url
        ? { ...item, cachedUrl }
        : item
    ) ?? [imageWithCache]

    setPreloadedProductData({
      productId: product.id,
      product: {
        ...product,
        image: imageWithCache,
        gallery: galleryWithCache,
      } satisfies Product,
      productIndex: eventProps.productIndex,
      eventListProps: eventProps.eventListProps,
      eventSearchProps: eventProps.eventSearchProps,
    })

    // tracking logic for recommendation service
    if (trackSlotClick && productSlotMap) {
      const slotId = productSlotMap.get(product.id)
      if (slotId) {
        trackSlotClick(slotId)
      }
    }

    if (queryID) {
      addProductQueryId(product.id, queryID)
    } else {
      // If user click on product not from PLP or search
      removeProductQueryId(product.id)
    }

    publish<EventPayloadType[EventType.PRODUCT_CLICKED]>(
      EventType.PRODUCT_CLICKED,
      eventPayload
    )
  }, [
    eventPayload,
    eventProps.eventListProps,
    eventProps.eventSearchProps,
    eventProps.productIndex,
    images.fit,
    images.plp.width,
    images.quality,
    product,
    productImage,
    productSlotMap,
    publish,
    queryID,
    setPreloadedProductData,
    trackSlotClick,
  ])

  const uniqueSizes = useMemo(() => {
    return product.variants ? getUniqueSizes(product.variants) : []
  }, [product.variants])

  const wishlistButtonProps = useMemo(() => {
    return {
      iconLeft: isInWishlist ? 'heart-full' : 'heart-empty',
      title: isInWishlist ? t('wishlist.remove-from') : t('wishlist.add-to'),
      ariaLabel: t('wishlist.add-product-to', { product: product.name }),
    }
  }, [isInWishlist, product.name, t])

  return {
    productImage,
    stockStatus,
    setProductData,
    uniqueSizes,
    isInWishlist,
    wishlistButtonProps,
    handleWishlistToggle,
    displayCardActions: true,
  }
}

const getUniqueSizes = (variants: ProductVariant[]) => [
  ...new Set(
    variants.flatMap((variant) =>
      variant.attributes
        .filter((attribute) => attribute.name === 'size')
        .map((size) => size.value)
    )
  ),
]
